<script setup lang="ts">
// Plays the joined party's current item and keeps it in sync with everyone else.
// Local play/pause/seek/skip are broadcast; remote state is applied with gentle drift
// correction (rate nudges for small drift, a seek for large).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { PartyMedia, PlayResponse } from '../api/types'
import { correction, expectedPosition, mediaKindOf, mediaTitle, syncLeader } from '../lib/party'
import { fetchAndDecrypt, keepAttachment, streamUrl } from '../lib/media'
import { localAttachment } from '../lib/attachmentStore'
import { useParty } from '../stores/party'
import { useSession } from '../stores/session'
import { displayName } from '../lib/format'
import { useBottomBar } from '../lib/bottomBar'
import MediaElement from './MediaElement.vue'
import Icon from './Icon.vue'
import PartyQueue from './PartyQueue.vue'

const party = useParty()
const session = useSession()
const el = ref<HTMLMediaElement | null>(null)
const source = ref<PlayResponse | null>(null)
const loadError = ref<string | null>(null)
const a = computed(() => party.active)
const item = computed<PartyMedia | undefined>(() => a.value?.party.queue[a.value.party.index])
const kind = computed(() => mediaKindOf(item.value))
const title = computed(() => mediaTitle(item.value))
const hasNext = computed(() => !!a.value && a.value.party.index + 1 < a.value.party.queue.length)
const showQueue = ref(false)
/** The browser refused to start sound without a tap (iOS Safari, or no interaction yet). */
const blocked = ref(false)
const root = ref<HTMLElement | null>(null)
useBottomBar('party', () => (kind.value === 'audio' ? root.value : null))

/** Until this time, media events are our own corrections — don't broadcast them. */
let quietUntil = 0
/** When someone last touched this player. Only their changes go to the room: a pause from
 * the system (headphones switching to call mode for a call, a device going away) stays
 * here, and the sync starts it again with everyone. */
let touched = 0
const touch = () => (touched = Date.now())
let timer: ReturnType<typeof setInterval> | null = null
let heartbeat: ReturnType<typeof setInterval> | null = null

async function resolve(m: PartyMedia): Promise<PlayResponse> {
  if (m.source === 'library') return session.playLibrary(m.item)
  const token = session.engine?.token ?? ''
  const host = party.joined ? session.conversations.get(party.joined.conv)?.host : undefined
  // Kept on this device: play that. Otherwise stream, and keep a copy while it plays
  // (playing is downloading, HRTH-110).
  const local = await localAttachment(m.media.id)
  if (local) return { url: URL.createObjectURL(local), hls: false, mime: m.media.mime, expires_at: 0 }
  void keepAttachment(m.media, token, host).catch(() => undefined)
  const url = (await streamUrl(m.media, token, host)) ?? URL.createObjectURL(await fetchAndDecrypt(m.media, token, host))
  return { url, hls: false, mime: m.media.mime, expires_at: 0 }
}

let resolving = 0
/** Fresh sources asked for since the item changed, after its player failed to load. */
let retries = 0
let retryTimer: ReturnType<typeof setTimeout> | undefined
const MAX_RETRIES = 3

async function load(): Promise<void> {
  const mine = ++resolving
  clearTimeout(retryTimer)
  // The old element goes with its source; the sync timer must not play it detached.
  el.value = null
  source.value = null
  loadError.value = null
  if (!item.value) return
  try {
    const s = await resolve(item.value)
    if (mine === resolving) source.value = s
  } catch (e) {
    if (mine === resolving) loadError.value = (e as Error).message
  }
}

watch(
  () => (item.value ? JSON.stringify(item.value) + (a.value?.party.index ?? 0) : null),
  () => {
    retries = 0
    void load()
  },
  { immediate: true },
)

/** The player couldn't load its source (a stream that failed once stays failed): a fresh one. */
function onMediaError(m: HTMLMediaElement): void {
  if (el.value !== m || retries >= MAX_RETRIES) return
  clearTimeout(retryTimer)
  retryTimer = setTimeout(() => void load(), 1000 * 2 ** retries++)
}

function applyRemote(): void {
  const media = el.value
  const p = a.value?.party
  if (!media || !p) return
  // Position needs metadata; playing doesn't (iOS loads nothing until asked to play).
  if (media.readyState >= 1) {
    const expected = expectedPosition(p, party.serverNow()) / 1000
    const c = correction(media.currentTime, expected, p.playing)
    if (c.seek !== undefined) {
      quietUntil = Date.now() + 800
      media.currentTime = Math.min(c.seek, Number.isFinite(media.duration) ? media.duration : c.seek)
    }
    media.playbackRate = c.rate
  }
  if (p.playing && media.paused) {
    if (blocked.value) return
    quietUntil = Date.now() + 800
    void media.play().catch((e: Error) => {
      if (e.name === 'NotAllowedError') blocked.value = true
    })
  } else if (!p.playing && !media.paused) {
    quietUntil = Date.now() + 800
    media.pause()
  }
}

/** A tap: the gesture the browser wanted before it would play sound. */
function listen(): void {
  blocked.value = false
  applyRemote()
}

function onElement(m: HTMLMediaElement): void {
  el.value = m
  m.addEventListener('playing', () => (blocked.value = false))
  m.addEventListener('loadedmetadata', applyRemote)
  m.addEventListener('error', () => onMediaError(m))
  const broadcast = () => {
    if (Date.now() < quietUntil || !a.value || Date.now() - touched > 3000) return
    void party.update({ position_ms: Math.round(m.currentTime * 1000), playing: !m.paused })
  }
  m.addEventListener('play', broadcast)
  m.addEventListener('pause', () => {
    if (!m.ended) broadcast()
  })
  m.addEventListener('seeked', broadcast)
  m.addEventListener('ended', () => {
    if (Date.now() < quietUntil) return
    if (hasNext.value) void next()
  })
}

async function next(): Promise<void> {
  if (!a.value) return
  quietUntil = Date.now() + 800
  await party.update({ index: a.value.party.index + 1, position_ms: 0, playing: true })
}

// Re-apply whenever anyone changes the state, and periodically to absorb drift.
watch(() => a.value?.party, applyRemote, { deep: true })
onMounted(() => {
  timer = setInterval(applyRemote, 2000)
  // While playing, one person repeats the state so late joiners and drift settle: the
  // first one present, so it carries on after the host leaves.
  heartbeat = setInterval(() => {
    const m = el.value
    const present = party.joined ? (session.inParty[party.joined.conv] ?? []) : []
    if (m && a.value && syncLeader(present, a.value.host) === session.me && !m.paused) {
      quietUntil = 0
      void party.update({ position_ms: Math.round(m.currentTime * 1000), playing: true })
    }
  }, 15_000)
})
onBeforeUnmount(() => {
  clearTimeout(retryTimer)
  if (timer) clearInterval(timer)
  if (heartbeat) clearInterval(heartbeat)
})

// The party ended (or we were the last to leave): close.
watch(a, (v) => {
  if (!v && party.joined) party.leave()
})
</script>

<template>
  <div
    v-if="a"
    ref="root"
    data-party-media
    @pointerdown.capture="touch"
    @pointerup.capture="touch"
    @keydown.capture="touch"
    :class="
      kind === 'video'
        ? 'fixed inset-0 z-40 flex items-center justify-center bg-black/95 p-3 md:p-4'
        : 'fixed right-0 bottom-0 left-0 z-30 border-t border-border-strong bg-bg-primary px-3 py-2 md:left-[60px] md:px-4'
    "
    data-testid="party-player"
  >
    <!-- Video: header (wraps its buttons under the title on phones), player, queue -->
    <div v-if="kind === 'video'" class="w-full max-w-4xl">
      <div class="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/20 pb-2 text-white">
        <span class="mono flex items-center gap-1.5 opacity-70" aria-hidden="true"><span class="h-1.5 w-1.5 animate-blink bg-signal" />WATCH</span>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm" data-testid="party-title">{{ title }}</div>
          <div class="mono truncate opacity-60">
            Watch party · started by {{ displayName(session.profiles, a.host) }}
            <template v-if="a.party.queue.length > 1"> · {{ a.party.index + 1 }}/{{ a.party.queue.length }}</template>
          </div>
        </div>
        <div class="flex shrink-0 flex-wrap items-center justify-end gap-1 max-sm:w-full">
          <button v-if="hasNext" class="btn-ghost text-white" @click="next">Next ›</button>
          <button class="btn-ghost text-white" :aria-pressed="showQueue" data-testid="party-queue-toggle" @click="showQueue = !showQueue">
            Queue · {{ a.party.queue.length }}
          </button>
          <button class="btn-ghost text-white" data-testid="party-leave" @click="party.leave()">Leave</button>
          <button v-if="a.host === session.me" class="btn-danger" data-testid="party-end" @click="party.end()">
            <span class="max-sm:hidden">End for everyone</span><span class="sm:hidden">End</span>
          </button>
        </div>
      </div>
      <button v-if="source && blocked" class="btn-primary mb-2 w-full" data-testid="party-listen" @click="listen">Tap to listen</button>
      <MediaElement v-if="source" :key="source.url" :play="source" kind="video" @element="onElement" />
      <p v-else-if="loadError" class="text-sm text-error">{{ loadError }}</p>
      <p v-else class="mono text-white/60">Loading…</p>
      <PartyQueue v-if="showQueue" class="mt-3 border border-white/20 bg-bg-primary" />
    </div>

    <!-- Audio: one row on wide screens; on phones what's playing and the actions, then the controls -->
    <div v-else class="flex flex-wrap items-center gap-x-2 gap-y-1 md:flex-nowrap md:gap-3">
      <div class="flex min-w-0 flex-1 items-center gap-2 md:max-w-64 md:gap-3">
        <span class="mono flex shrink-0 items-center gap-1.5 opacity-70" aria-hidden="true"><span class="h-1.5 w-1.5 animate-blink bg-signal" /><span class="max-sm:hidden">ROOM</span></span>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm" data-testid="party-title">{{ title }}</div>
          <div class="mono truncate opacity-60">
            Listening room · {{ displayName(session.profiles, a.host) }}
            <template v-if="a.party.queue.length > 1"> · {{ a.party.index + 1 }}/{{ a.party.queue.length }}</template>
          </div>
        </div>
      </div>
      <div class="flex min-w-0 basis-full items-center gap-2 max-md:order-last md:basis-auto md:flex-1">
        <button v-if="source && blocked" class="btn-primary shrink-0" data-testid="party-listen" @click="listen">Tap to listen</button>
        <MediaElement v-if="source" :key="source.url" :play="source" kind="audio" class="min-w-0 flex-1" @element="onElement" />
        <p v-else-if="loadError" class="truncate text-sm text-error">{{ loadError }}</p>
        <p v-else class="mono opacity-60">Loading…</p>
      </div>
      <div class="flex shrink-0 items-center">
        <button v-if="hasNext" class="player-btn" aria-label="Next track" title="Next track" data-testid="party-next" @click="next">
          <Icon name="next" />
        </button>
        <button
          class="player-btn w-auto gap-1 px-2 max-md:w-auto"
          :class="showQueue ? 'text-text-primary' : ''"
          :aria-pressed="showQueue"
          :aria-label="`Queue, ${a.party.queue.length}`"
          title="Queue"
          data-testid="party-queue-toggle"
          @click="showQueue = !showQueue"
        >
          <Icon name="queue" /><span class="mono">{{ a.party.queue.length }}</span>
        </button>
        <button class="btn-ghost px-2" data-testid="party-leave" @click="party.leave()">Leave</button>
        <button v-if="a.host === session.me" class="btn-ghost px-2 text-error hover:text-error" title="End for everyone" data-testid="party-end" @click="party.end()">
          End
        </button>
      </div>
      <PartyQueue
        v-if="showQueue"
        class="panel absolute right-0 bottom-full left-0 mb-px border-border-strong md:right-4 md:left-auto md:mb-1 md:w-80"
      />
    </div>
  </div>
</template>
